import type { ClientRow } from '@busy/api';

// Shared sort + group helpers for the alphabetized client roster used by the
// Clients route, the Settings → Clients panel, and the Cut Timer's check-in
// step. One source of truth so all three surfaces order names identically
// (Apple-Contacts default: last name, then first name, case-insensitive).

const ALL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Sort key: "lastname firstname" lowercase. Falls back to phone digits when
 *  both name fields are empty. Anonymous Client#### entries naturally land
 *  after their first letter ('a'). */
export function clientSortKey(c: ClientRow): string {
  const last = (c.last_name ?? '').trim();
  const first = (c.first_name ?? '').trim();
  return (last + ' ' + first).trim().toLowerCase() || (c.phone ?? '').toLowerCase();
}

/** First letter of the sort key, uppercased. Anything outside A-Z bucketed
 *  as '#'. */
export function clientFirstLetter(c: ClientRow): string {
  const k = clientSortKey(c);
  const first = k.charAt(0).toUpperCase();
  return ALL_LETTERS.includes(first) ? first : '#';
}

/** Returns rows grouped into sections by first letter, in canonical order
 *  (A-Z then '#'). Each section retains the input's relative order — sort
 *  upstream if you want alphabetical-within-section. */
export function groupClientsByLetter(rows: ClientRow[]): Array<readonly [string, ClientRow[]]> {
  const map = new Map<string, ClientRow[]>();
  for (const c of rows) {
    const letter = clientFirstLetter(c);
    const bucket = map.get(letter) ?? [];
    bucket.push(c);
    map.set(letter, bucket);
  }
  return [
    ...ALL_LETTERS.filter((L) => map.has(L)).map((L) => [L, map.get(L)!] as const),
    ...(map.has('#') ? ([['#', map.get('#')!]] as const) : []),
  ];
}

export const ALPHA_LETTERS = ALL_LETTERS;
